// Pure 2D helpers shared by the model, the rasteriser and the editor. Sheet coordinates throughout.

import type { Part } from '../types';
import { PARTS } from './parts';

export type Pt = [number, number];
export interface Bounds { minX: number; minY: number; maxX: number; maxY: number }

export const dist = (a: Pt, b: Pt) => Math.hypot(a[0] - b[0], a[1] - b[1]);

export function bounds(polys: Pt[][]): Bounds {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const poly of polys) for (const [x, y] of poly) {
    if (x < minX) minX = x; if (x > maxX) maxX = x;
    if (y < minY) minY = y; if (y > maxY) maxY = y;
  }
  return { minX, minY, maxX, maxY };
}

export function signedArea(poly: Pt[]): number {
  let a = 0;
  for (let i = 0; i < poly.length; i++) {
    const p = poly[i], q = poly[(i + 1) % poly.length];
    a += p[0] * q[1] - q[0] * p[1];
  }
  return a / 2;
}

export function windingNumber(p: Pt, poly: Pt[]): number {
  let w = 0;
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i], b = poly[(i + 1) % poly.length];
    const cross = (b[0] - a[0]) * (p[1] - a[1]) - (p[0] - a[0]) * (b[1] - a[1]);
    if (a[1] <= p[1]) { if (b[1] > p[1] && cross > 0) w++; }
    else if (b[1] <= p[1] && cross < 0) w--;
  }
  return w;
}

export const pointInPoly = (p: Pt, poly: Pt[]) => windingNumber(p, poly) !== 0;
export const pointInPolys = (p: Pt, polys: Pt[][]) => polys.reduce((w, poly) => w + windingNumber(p, poly), 0) !== 0;

export function distToSegment(p: Pt, a: Pt, b: Pt): number {
  return dist(p, projectOnSegment(p, a, b));
}

export function projectOnSegment(p: Pt, a: Pt, b: Pt): Pt {
  const dx = b[0] - a[0], dy = b[1] - a[1], l2 = dx * dx + dy * dy;
  const t = l2 ? Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / l2)) : 0;
  return [a[0] + dx * t, a[1] + dy * t];
}

export function distToPolyline(p: Pt, pts: Pt[], closed: boolean): number {
  let best = Infinity;
  const n = closed ? pts.length : pts.length - 1;
  for (let i = 0; i < n; i++) best = Math.min(best, distToSegment(p, pts[i], pts[(i + 1) % pts.length]));
  if (pts.length === 1) best = dist(p, pts[0]);
  return best;
}

export function segmentIntersection(a: Pt, b: Pt, c: Pt, d: Pt): Pt | null {
  const rx = b[0] - a[0], ry = b[1] - a[1], sx = d[0] - c[0], sy = d[1] - c[1];
  const den = rx * sy - ry * sx;
  if (Math.abs(den) < 1e-12) return null;
  const t = ((c[0] - a[0]) * sy - (c[1] - a[1]) * sx) / den;
  const u = ((c[0] - a[0]) * ry - (c[1] - a[1]) * rx) / den;
  return t >= 0 && t <= 1 && u >= 0 && u <= 1 ? [a[0] + rx * t, a[1] + ry * t] : null;
}

export function pathLength(pts: Pt[], closed = false): number {
  let l = 0;
  for (let i = 1; i < pts.length; i++) l += dist(pts[i - 1], pts[i]);
  if (closed && pts.length > 1) l += dist(pts[pts.length - 1], pts[0]);
  return l;
}

// First self-crossing of a drawn line that encloses a worthwhile loop: the loop's points, or null.
export function findLoop(pts: Pt[], minPerimeter = 0.15): Pt[] | null {
  for (let i = 0; i < pts.length - 3; i++) {
    for (let j = i + 2; j < pts.length - 1; j++) {
      const x = segmentIntersection(pts[i], pts[i + 1], pts[j], pts[j + 1]);
      if (!x) continue;
      const loop: Pt[] = [x, ...pts.slice(i + 1, j + 1)];
      if (pathLength(loop, true) >= minPerimeter) return loop;
    }
  }
  return null;
}

export function chaikin(pts: Pt[], closed: boolean): Pt[] {
  if (pts.length < 3) return pts.slice();
  const out: Pt[] = [];
  const n = pts.length, last = closed ? n : n - 1;
  if (!closed) out.push(pts[0]);
  for (let i = 0; i < last; i++) {
    const a = pts[i], b = pts[(i + 1) % n];
    const q: Pt = [0.75 * a[0] + 0.25 * b[0], 0.75 * a[1] + 0.25 * b[1]];
    const r: Pt = [0.25 * a[0] + 0.75 * b[0], 0.25 * a[1] + 0.75 * b[1]];
    if (!closed && i === 0) out.push(r);
    else if (!closed && i === last - 1) out.push(q);
    else out.push(q, r);
  }
  if (!closed) out.push(pts[n - 1]);
  return out;
}

export function rdp(pts: Pt[], eps: number): Pt[] {
  if (pts.length < 3) return pts.slice();
  const keep = new Uint8Array(pts.length);
  keep[0] = keep[pts.length - 1] = 1;
  const stack: [number, number][] = [[0, pts.length - 1]];
  while (stack.length) {
    const [s, e] = stack.pop()!;
    let worst = -1, wi = -1;
    for (let i = s + 1; i < e; i++) {
      const d = distToSegment(pts[i], pts[s], pts[e]);
      if (d > worst) { worst = d; wi = i; }
    }
    if (worst > eps && wi > 0) { keep[wi] = 1; stack.push([s, wi], [wi, e]); }
  }
  return pts.filter((_, i) => keep[i]);
}

// Smooth once, simplify, and keep doubling the tolerance until the stroke fits the point cap.
export function simplifyStroke(raw: Pt[], closed: boolean, cap = 60): Pt[] {
  const min = closed ? 3 : 2;
  const run = (pts: Pt[], eps: number) => closed ? rdp([...pts, pts[0]], eps).slice(0, -1) : rdp(pts, eps);
  let pts = run(raw, 0.003);
  pts = chaikin(pts, closed);
  let eps = 0.004, out = run(pts, eps);
  while (out.length > cap) { eps *= 1.4; out = run(pts, eps); }
  return out.length >= min ? out : pts.slice(0, Math.max(min, 2));
}

export function circlePoly(cx: number, cy: number, r: number, seed: number, n = 20, irregular = 0.08): Pt[] {
  let a = (seed * 2654435761) >>> 0;
  const rnd = () => { a = (Math.imul(a, 1664525) + 1013904223) >>> 0; return a / 4294967296; };
  const phase = rnd() * Math.PI * 2;
  return Array.from({ length: n }, (_, i) => {
    const t = phase + (i / n) * Math.PI * 2, k = 1 + (rnd() - 0.5) * 2 * irregular;
    return [cx + Math.cos(t) * r * k, cy + Math.sin(t) * r * k] as Pt;
  });
}

// ---- part paths

export function flattenPath(d: string): Pt[][] {
  const tokens = d.match(/[MLQCZ]|-?\d*\.?\d+(?:e-?\d+)?/g) ?? [];
  const subs: Pt[][] = [];
  let cur: Pt[] = [], i = 0, cmd = '';
  const num = () => parseFloat(tokens[i++]);
  const pt = (): Pt => [num(), num()];
  while (i < tokens.length) {
    if (/[MLQCZ]/.test(tokens[i])) cmd = tokens[i++];
    if (cmd === 'Z') { if (cur.length) subs.push(cur); cur = []; continue; }
    if (cmd === 'M') { if (cur.length) subs.push(cur); cur = [pt()]; cmd = 'L'; continue; }
    const from = cur[cur.length - 1];
    if (cmd === 'L') cur.push(pt());
    else if (cmd === 'Q') {
      const c = pt(), e = pt();
      for (let k = 1; k <= 6; k++) {
        const t = k / 6, u = 1 - t;
        cur.push([u * u * from[0] + 2 * u * t * c[0] + t * t * e[0], u * u * from[1] + 2 * u * t * c[1] + t * t * e[1]]);
      }
    } else if (cmd === 'C') {
      const c1 = pt(), c2 = pt(), e = pt();
      for (let k = 1; k <= 8; k++) {
        const t = k / 8, u = 1 - t;
        cur.push([
          u * u * u * from[0] + 3 * u * u * t * c1[0] + 3 * u * t * t * c2[0] + t * t * t * e[0],
          u * u * u * from[1] + 3 * u * u * t * c1[1] + 3 * u * t * t * c2[1] + t * t * t * e[1],
        ]);
      }
    }
  }
  if (cur.length) subs.push(cur);
  return subs.filter(s => s.length > 2);
}

// Outer rings wind one way, holes (rings sitting wholly inside another) the other, so a nonzero fill is right.
function normaliseWinding(subs: Pt[][]): Pt[][] {
  return subs.map((s, i) => {
    const depth = subs.filter((o, j) => j !== i && s.every(p => windingNumber(p, o) !== 0)).length;
    const area = signedArea(s);
    return (depth % 2 === 0) === (area > 0) ? s : s.slice().reverse();
  });
}

const cache = new Map<string, Pt[][]>();

export function partPolys(shape: string): Pt[][] {
  let c = cache.get(shape);
  if (!c) {
    const def = PARTS[shape];
    c = def ? normaliseWinding(flattenPath(def.d)) : [];
    cache.set(shape, c);
  }
  return c;
}

export function placePoint(part: Part, p: Pt): Pt {
  const ux = (part.flip ? -p[0] : p[0]) * part.scale, uy = p[1] * part.scale;
  const c = Math.cos(part.rotate), s = Math.sin(part.rotate);
  return [part.x + ux * c - uy * s, part.y + ux * s + uy * c];
}

export function partWorldPolys(part: Part): Pt[][] {
  return partPolys(part.shape).map(poly => poly.map(p => placePoint(part, p)));
}

export function polysToD(polys: Pt[][], flipY = false): string {
  return polys.map(p => 'M' + p.map(([x, y]) => `${x.toFixed(3)} ${(flipY ? -y : y).toFixed(3)}`).join('L') + 'Z').join('');
}
